import type { ReactNode } from "react";
import { useLocation } from "react-router";

import { cn } from "@/lib/cn";
import { FavouriteButton } from "@/components/ui/FavouriteButton";

export interface PageHeaderProps {
  title: string;
  actions?: ReactNode;
  /**
   * Whether the screen can be pinned to the sidebar from here. On unless a
   * page says otherwise: a form half filled in, or a step on the way to
   * somewhere, is not a place anybody comes back to.
   */
  pinnable?: boolean;
  className?: string;
}

/**
 * The top of a page.
 *
 * A title and whatever you can do here, and nothing else. There used to be a
 * line of explanation under every one of them, which is how a slide deck
 * introduces a slide; somebody using this eight hours a day already knows what
 * the rota is. On a narrow screen the actions drop under the title rather than
 * squeezing it. The star beside the title pins the screen to the sidebar, as
 * it stands: the address with whatever filter or section is in it.
 */
export function PageHeader({ title, actions, pinnable = true, className }: PageHeaderProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <h1 className="min-w-0 text-2xl font-bold text-fg">{title}</h1>
        {pinnable ? <PageStar title={title} /> : null}
      </div>
      {actions ? (
        // Not for paper: what you can do here is not part of what is here.
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-print="hide">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The star for the screen that is open. Its own component, so a header that
 * cannot be pinned needs neither a router nor somebody signed in.
 */
function PageStar({ title }: { title: string }) {
  const location = useLocation();
  return <FavouriteButton to={`${location.pathname}${location.search}`} label={title} />;
}
